(ns dashboard.views.browser-extension.questionnaire.view
  (:require [clojure.string :as str]
            [dashboard.browser-extension :as browser-extension]
            [dashboard.entity.analysis.policy-process-pairs :as process<->policy]
            [dashboard.entity.question.profile :as question.profile]
            [dashboard.entity.questionnaire.db :as questionnaire.db]
            [dashboard.html :as html]
            [dashboard.services.section :as section-services]
            [dashboard.subs]
            [dashboard.views.browser-extension.questionnaire.events :as events]
            [dashboard.views.browser-extension.questionnaire.questions.view :as questions.view]
            [dashboard.views.browser-extension.questionnaire.subs :as subs]
            [re-frame.core :as rf]
            [react]))

(rf/reg-event-db
 ::update-question-element-ref
 (fn [db [_ question-id el]]
   (assoc-in db [:browser-extension :question-elements question-id] el)))

(rf/reg-sub
 ::selected-section
 (fn [db [_]]
   (get-in db [:browser-extension :selected-section])))

(rf/reg-sub
 ::selected-question
 (fn [db [_]]
   (get-in db [:browser-extension :selected-question])))

(rf/reg-sub
 ::question-tree
 (fn [db [_ questionnaire-id question-filter-fn]]
   (as-> db v
     (get-in v [:questionnaires questionnaire-id :questions :data])
     (filter question-filter-fn v)
     (sort-by :ordinal v)
     (questionnaire.db/make-trees v))))

(rf/reg-sub
 ::question-profile
 (fn [db [_ questionnaire-id question-id]]
   (->> (get-in db [:questionnaires questionnaire-id :questions :data])
        (filter (comp (partial = question-id) :id))
        first)))


(defn question-editor
  "Wrapper for the question answering component"
  [question on-edit-question reorder-enabled? next-id previous-id]
  [:<>
   {:key (keyword :question-row (:id question))}
   [:div.is-flex.mt-4.has-background-tertiary.p-2
    [:h5.title.is-5.is-color-white.my-0
     [:button.button.is-flex-grow-1.is-secondary.mr-3
      {:on-click (fn []
                   (on-edit-question previous-id true))}
      [:i.icon.fas.fa-caret-left]]
     [:button.button.is-flex-grow-1.is-secondary.mr-3
      {:on-click #(on-edit-question (:id question))}
      [:i.icon.fas.fa-table-list]]
     [:a
      {:on-click #(rf/dispatch [::browser-extension/select-question (:content question) false])}
      "* Active Question"
      [:i.icon.fas.fa-caret-down.ml-2]]]
    [:div.is-flex.is-justify-content-flex-end.is-flex-grow-1

     (when-not reorder-enabled?
       [:button.button.is-flex-grow-0.is-secondary.mr-3
        {:on-click (fn []
                     (on-edit-question next-id true))}
        [:i.icon.fas.fa-caret-right]])

     (when-not reorder-enabled?
       [:a.button.is-small.is-rounded.is-circular.is-secondary
        {:on-click #(rf/dispatch [::browser-extension/select-question (:content question) false])
         :title "Select this question"}
        [:span.icon>i.fas.fa-hand-pointer]])
     (when-not reorder-enabled?
       [:a.button.is-small.is-rounded.is-circular.ml-1.is-secondary
        {:on-click #(rf/dispatch [::browser-extension/delete-question question])
         :title "Delete this question"}
        [:span.icon>i.fas.fa-trash]])]]
   [:f> questions.view/question-row
    (:questionnaire-id question)
    (:id question)
    #(rf/dispatch [::browser-extension/select-question (:content question) false])]
   [:div.is-flex
    [:button.button.is-danger.is-outlined.mt-2.is-flex-grow-1.mb-2
     {:on-click (fn [e]
                  (.preventDefault e)
                  (on-edit-question (:id question)))}
     "Close Editor"]]
   [:div.is-flex.mt-4.has-background-active.p-2
    [:h5.title.is-5.is-color-white.my-0
     "* "
     [:i.icon.fas.fa-caret-up.ml-2]]]])

(defn question-row
  "The non-editable version of a question which displays only the question for
   display, but also offers togglable reordering and hierarchy tools"
  [question depth on-edit-question]
  (let [{:keys [questionnaire-id id content follow-ups data]} question
        selected-question @(rf/subscribe [::selected-question])
        selected? (= id (:id selected-question))
        reorder-enabled? @(rf/subscribe [::subs/question-reorder-enabled])
        saving? @(rf/subscribe [::subs/has-question-restore-state id])]
    [:<>
     {:key (keyword :question-row (:id question))}
     [:div.table-row.px-1.py-0.my-2
      {:ref (fn [el]
              (when el
                (rf/dispatch [::update-question-element-ref id el])))
       :style (cond-> {:border-radius "4px"
                       :padding-left "0.25rem"
                       :user-select "text"
                       :margin-left (str (* depth 2) "rem")})
       :class (if selected?
                "has-background-highlight"
                "has-background-secondary")}

      [:f> question.profile/browser-extension-question
       question
       #(rf/dispatch [::browser-extension/select-question content false])
       [:div.level-right
        [:div.is-flex.px-0
         (if-not reorder-enabled?
           [:<>
            [question.profile/Status question
             #(questionnaire.db/update-question-status questionnaire-id
                                                       id
                                                       %)]
            [:a.button.is-small.is-rounded.is-circular.ml-1
             {:on-click #(rf/dispatch [::browser-extension/delete-question question])
              :title "Delete this question"}
             [:span.icon>i.fas.fa-trash]]]
           [:<>
            (:ordinal question)
            [:a.button.is-small.is-rounded.is-circular.ml-1
             {:on-click #(rf/dispatch [::events/move-question questionnaire-id id :move-to-parent])
              :title "Move this question into the parent's hierarchy level"}
             [:span.icon>i.fas.fa-caret-left]]
            [:a.button.is-small.is-rounded.is-circular.ml-1
             {:on-click #(rf/dispatch [::events/move-question questionnaire-id id :follow-up])
              :title "Move this question as child of nearest question"}
             [:span.icon>i.fas.fa-caret-right]]
            [:a.button.is-small.is-rounded.is-circular.ml-1
             {:on-click #(rf/dispatch [::events/move-question questionnaire-id id :before])
              :title "Move this question upwards"}
             [:span.icon>i.fas.fa-caret-up]]
            [:a.button.is-small.is-rounded.is-circular.ml-1
             {:on-click #(rf/dispatch [::events/move-question questionnaire-id id :after])
              :title "Move this question downwards"}
             [:span.icon>i.fas.fa-caret-down]]])
         (when saving?
           [:div.loader {:style {:border-width "0.5rem"
                                 :width "1rem"
                                 :height "1rem"}}])]]]

      [:div.is-size-6.is-mobile
       {:key (keyword :question-row-child (:id question))}
       (when (seq follow-ups)
         [:<>
          (->> (map-indexed
                (fn [i child-question]
                  #^{:key (str "follow-up-question-row-" (:id child-question) "-" i)}
                  [question-row
                   (assoc child-question :display-name (str (:display-name question) "." (inc i)))
                   0
                   on-edit-question])
                follow-ups)
               (into [:<>]))])]]]))

(defn Section
  [{:keys [id name ordinal] :as section} section-count]
  (let [questionnaire-id (-> @(rf/subscribe [::dashboard.subs/route]) :parameters :path :id uuid)
        selected-section @(rf/subscribe [::selected-section])
        questions @(rf/subscribe [::question-tree questionnaire-id #(= id (:section-id %))])
        selected-question @(rf/subscribe [::selected-question])
        question-profile (when selected-question
                           @(rf/subscribe [::question-profile questionnaire-id (:id selected-question)]))
        selected? (= id (:id selected-section))
        question-reorder-enabled? @(rf/subscribe [::subs/question-reorder-enabled])
        section-reorder-enabled? @(rf/subscribe [::subs/section-reorder-enabled])
        [goto-question-ref set-goto-question-ref] (react/useState nil)
        [last-selected-question-id set-last-selected-question-id] (react/useState nil)
        [ref set-ref] (react/useState nil)
        on-edit-question (fn [question-id & [force]]
                           (if force
                             (let [question-text (->> questions
                                                      (filter (comp (partial = question-id) :id))
                                                      first
                                                      :content)]
                              (rf/dispatch [::browser-extension/select-question question-text false]))
                             (if (= (:id selected-question) question-id)
                               (do
                                 (set-last-selected-question-id (:id selected-question))
                                 (rf/dispatch [::browser-extension/deselect-question (:content selected-question) false]))
                               (rf/dispatch [::browser-extension/select-question (:content selected-question) false]))))]
    (react/useEffect
     (fn [_]
       (questions.view/load-controller questionnaire-id)
       (process<->policy/fetch-policy-process-pairs))
     #js [nil])

    (react/useEffect
     (fn [_]
       (when ref
         (.scrollIntoView ref #js{:behavior "smooth"
                                  :block "center"})))
     #js [ref])

    (react/useEffect
     (fn [_]
       (when (and last-selected-question-id
                  goto-question-ref)
         (.scrollIntoView goto-question-ref
                          #js{;:behavior "smooth"
                              :block "center"})
         (set-last-selected-question-id nil)))
     #js [last-selected-question-id goto-question-ref])

    [:div.is-flex-grow-1.is-align-items-start
     (when selected? {:ref set-ref})
     (when (or (not selected-question) selected?)
       [:div.is-tertiary.px-2.py-4.mt-2.is-flex-grow-1
        {:style {:display "flex"
                 :align-items "center"
                 :position "sticky"
                 :top "0px"
                 :z-index 10}
         :class (when selected?
                  ["has-background-info" "has-text-white"])}
        [:a
         {:on-click #(when-not (or section-reorder-enabled?
                                   question-reorder-enabled?)
                       (if selected?
                        (rf/dispatch [::browser-extension/select-section nil false])
                        (rf/dispatch [::browser-extension/select-section section false])))
          :style {:border-radius "4px 4px 0px 0px"
                  :flex-grow "1"
                  :cursor (if (or section-reorder-enabled? question-reorder-enabled?)
                            "default"
                            "pointer")
                  :pointer-events (if section-reorder-enabled?
                                    "none"
                                    "auto")}
          :class (when selected?
                   ["has-background-info" "has-text-white"])
          :title (when-not (or section-reorder-enabled? question-reorder-enabled?)
                   "Select this section")}
         [:strong.is-size-6
          {:style {:pointer-events (if section-reorder-enabled?
                                     "none"
                                     "auto")}}
          (html/as-hiccup-with-anchors name)]]
        (when (not (or section-reorder-enabled? question-reorder-enabled?))
          [:<>
           (when selected?
             [:button.button.is-small.is-rounded.is-circular.mr-2
              {:on-click #(when-let [new-question (js/prompt "Enter the question text")]
                            (rf/dispatch [::browser-extension/create-question new-question new-question false]))
               :title "Create a new Question from text"}
              [:i.icon.fas.fa-circle-plus]])
           [:a.button.is-small.is-rounded.is-circular
            {:on-click #(rf/dispatch [::browser-extension/delete-section section])
             :title "Delete this section"}
            [:span.icon>i.fas.fa-trash]]])
        (when section-reorder-enabled?
          [:<>
           ordinal
           [:button.button.is-small.is-rounded.is-circular.ml-1
            {:on-click #(rf/dispatch [::section-services/move-section questionnaire-id section (-> section :ordinal dec)])
             :disabled (<= ordinal 0)
             :title "Move this Section upwards"}
            [:span.icon>i.fas.fa-caret-up]]
           [:button.button.is-small.is-rounded.is-circular.ml-1
            {:on-click #(rf/dispatch [::section-services/move-section questionnaire-id section (-> section :ordinal inc)])
             :disabled (>= ordinal (dec section-count))
             :title "Move this section downwards"}
            [:span.icon>i.fas.fa-caret-down]]])])
     (when (and selected?
                (not section-reorder-enabled?))
       (if (and (seq questions)
                (not section-reorder-enabled?))
         [:div.table.px-1
          (if (:id selected-question)
            (let [id->index (->> questions
                               (map-indexed #(vector (:id %2) %1))
                               (into {}))
                  index->id (->> questions
                                 (map-indexed #(vector %1 (:id %2)))
                                 (into {}))
                  current-index (id->index (:id selected-question))
                  next-id (get index->id (inc current-index))
                  previous-id (get index->id (dec current-index))]
             [:f> question-editor
              question-profile
              on-edit-question
              question-reorder-enabled?
              next-id
              previous-id])
            (into [:<>]
                  (map-indexed
                   (fn [i question]
                     #^{:key (str "question-row-" (:id question) "-" i)}
                       [:div
                        (when (= (:id question) last-selected-question-id)
                          {:ref set-goto-question-ref})
                        [question-row
                         (assoc question :display-name (inc i))
                         0
                         on-edit-question]])
                   questions)))]
         [:p.p-2.has-text-grey "No questions defined for this section."]))]))

(defn no-questions []
  [:div.section.is-flex.is-flex-direction-column.is-align-items-center
   [:b "Nothing here yet"]
   [:img {:src "/img/fly.svg" :width "150px"}]
   [:p.has-text-centered "Get started by highlighting and marking questions in the portal"]])

(defn Sections [sections]
  (if (seq sections)
    [:div
     {:style {:overflow-y "scroll"
              :height "100vh"
              :padding-bottom "40vh"}}
     (doall (map (fn [section]
                   #^{:key (str "outer-section-" (:id section))}
                     [:f> Section section (count sections)])
                 sections))
     [:div.is-flex.ml-2
      [:button.button.is-secondary.is-rounded.mt-2.mr-2
       {:on-click #(when-let [new-name (js/prompt "Enter the name of the new section")]
                     (rf/dispatch [::browser-extension/create-section nil new-name]))
        :title "Create a new Section from text"}
       [:i.icon.fas.fa-circle-plus.mr-2]
       "New Section"]]]
    [no-questions]))

(defn questionnaire-page []
  (let [id (-> @(rf/subscribe [::dashboard.subs/route]) :parameters :path :id uuid)
        questionnaire @(questionnaire.db/questionnaire* id)
        sections @(rf/subscribe [::browser-extension/sections id])
        question-reorder-enabled? @(rf/subscribe [::subs/question-reorder-enabled])
        section-reorder-enabled? @(rf/subscribe [::subs/section-reorder-enabled])
        selected-section @(rf/subscribe [::selected-section])
        selected-question @(rf/subscribe [::selected-question])
        {:keys [custom-name filename status url]} (-> questionnaire :metadata :data)
        [expanded set-expanded] (react/useState false)
        questions @(rf/subscribe [::question-tree id #(= (:id selected-section) (:section-id %))])]
    (react/useEffect
     (fn []
       (when question-reorder-enabled?
         (rf/dispatch [::events/toggle-reorder]))
       (rf/dispatch [::browser-extension/select-section nil false])
       (rf/dispatch [::browser-extension/select-question nil false]))
     #js [nil])

    (react/useEffect
     (fn []
       (rf/dispatch [::browser-extension/send-event
                     {:type :questionnaire-selected
                      :questionnaire questionnaire
                      :sections sections}])
       (fn []))
     (array questionnaire questions))

    (react/useEffect
     (fn [_]
       (rf/dispatch [::browser-extension/send-event
                     {:type :update-selected-question-data
                      :data selected-question}])
       nil)
     #js [questionnaire selected-question])

    [:div.section.pt-6.px-0.is-flex.is-flex-direction-column.is-flex-grow-1
     [:div.level.is-mobile.mb-1.px-2
      [:div.level-left>span.level-item.title.is-5 (or custom-name filename)]
      [:div.level-right
       [:button.button.is-secondary.is-rounded.is-small.mt-2
        {:on-click #(set-expanded not)}
        (if expanded
          [:i.icon.fas.fa-caret-up]
          [:i.icon.fas.fa-caret-down])]
       [:span.level-item.tag.ml-2
        {:class [(case status "approved" "is-success" "in-progress" "is-warning" "")]}
        (if status (str/capitalize status) "N/A")]]]
     (when expanded
       [:p.subtitle.is-7.px-2.py-0.my-0 url])

     (when (or expanded
               question-reorder-enabled?
               section-reorder-enabled?)
       [:div.level.px-2
        [:a.button.is-small.is-rounded
         {:on-click #(rf/dispatch [::events/toggle-reorder])
          :class (if question-reorder-enabled? "is-warning" "is-outlined")
          :disabled section-reorder-enabled?
          :title (if question-reorder-enabled?
                   "Click to deactivate question reordering mode"
                   (str "Click to activate question reordering mode.\n\n"
                        "Reorder the questions by clicking the grid icon next to the question and dragging it.\n\n"
                        "When dragging a question, a green full-width bar indicates where the question will be placed if mouse is released.\n\n"
                        "A narrower green bar means that the question will be placed as a follow-up question to the question above the bar.\n\n"
                        "You can also move a question to a different section."))}
         [:span.mr-1 "Reorder questions"]
         [:span.icon>i.fas.fa-sort]]
        [:a.button.is-small.is-rounded.ml-2
         {:on-click #(rf/dispatch [::events/toggle-reorder-section])
          :class (if section-reorder-enabled? "is-warning" "is-outlined")
          :disabled question-reorder-enabled?
          :title (if section-reorder-enabled?
                   "Click to deactivate section reordering mode"
                   (str "Click to activate section reordering mode\n\n"
                        "Reorder sections by dragging from the grid icon, and dropping the section onto the place where it should be moved to.\n\n"
                        "Sections between the start and end of move will re-arranged accordingly."))}
         [:span.mr-1 "Reorder sections"]
         [:span.icon>i.fas.fa-sort]]])
     [:f> Sections sections]]))
